import { useMutation, useQueryClient, useInfiniteQuery } from '@tanstack/react-query';
import { trackError } from 'Utils/errorMonitoring';
import { getSuggestions, acceptSuggestion as acceptSuggestionService, rejectSuggestion as rejectSuggestionService } from 'Services/ai';
import { RESULTS_PER_PAGE } from 'Utils/constants';

export default function useInfiniteAiSuggestions() {
    const { data, isLoading, isFetching, fetchNextPage, hasNextPage } = useInfiniteQuery(['ai-suggestions'],
        async ({ pageParam = 1 }) => {
            const response = await getSuggestions({ page: pageParam });
            return response;
        }, {
        refetchOnWindowFocus: false,
        onError: (err) => trackError(err),
        getNextPageParam: (lastPage, allPages) => {
            const pagesLoaded = allPages.length;
            const totalPages = Math.ceil(lastPage.count / RESULTS_PER_PAGE);
            return pagesLoaded < totalPages ? pagesLoaded + 1 : false;
        },
    });

    const queryClient = useQueryClient();

    const { mutate: acceptSuggestion } = useMutation({
        mutationFn: ({ suggestionId, data }) => acceptSuggestionService(suggestionId, data),
        onMutate: ({ suggestionId }) => {
            queryClient.setQueryData(['ai-suggestions'], (oldData) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => {
                        return {
                            ...page,
                            count: Math.max(0, page.count - 1),
                            items: page.items.filter(item => item.id !== suggestionId),
                        };
                    }),
                }
            });
        },
        onError: (error) => {
            trackError(error);
        },
    });

    const { mutate: rejectSuggestion } = useMutation({
        mutationFn: ({ suggestionId }) => rejectSuggestionService(suggestionId),
        onMutate: ({ suggestionId }) => {
            queryClient.setQueryData(['ai-suggestions'], (oldData) => {
                if (!oldData) return oldData;

                return {
                    ...oldData,
                    pages: oldData.pages.map((page) => {
                        return {
                            ...page,
                            count: Math.max(0, page.count - 1),
                            items: page.items.filter(item => item.id !== suggestionId),
                        };
                    }),
                }
            });
        },
        onError: (error) => {
            trackError(error);
        },
    });

    return {
        data,
        isLoading,
        acceptSuggestion,
        rejectSuggestion,
        isFetching,
        fetchNextPage,
        hasNextPage
    };
}
